import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import PageHeader from "../../components/page-header";
import {useGoal} from "../../../models/goal/use-goal";
import {useDispatch, useSelector} from "react-redux";
import {useProject} from "../../../models/project/use-project";
import {useWorkspace} from "../../../models/workspace/use-workspace";
import ZofinoGraph from "../../../system/components/zofino-graph";
import {useTranslate} from "../../../system/useTranslate";
import {Handle, Position} from "@xyflow/react";
import Icon from "../../../system/components/icon";
import {Divider} from "primereact/divider";
import Avatar from "../../../system/components/avatar";
import {Menu} from "primereact/menu";
import {normalizeApiError} from "../../../system/api";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {useFormik} from "formik";
import {MultiSelect} from "primereact/multiselect";
import {OverlayPanel} from "primereact/overlaypanel";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import SelectCycle from "../components/select-cycle";
import {setMessage} from "../../../system/app-slice";
import {useWorkspaceRole} from "../../../system/use-workspace-role";
import {isWorkspaceReadOnlyRole} from "../../../system/workspace-permissions";
import {usePermission} from "../../../system/usePermission";
import {usePublishAiScope} from "../../../system/ai-scope-context";
import {
    canEnterStrategyMapEntity,
    filterStrategyMapEntities,
} from "./strategy-map-access";
import {
    getStrategyStatusColor,
    normalizeStrategyStatus,
    STRATEGY_STATUS,
    STRATEGY_STATUS_COLORS,
    withAlpha,
} from "../../../system/strategy-map-status";
import GoalReparentDialog from "../components/goal-reparent-dialog";
import {MISSION_PARENT} from "../components/goal-parent-select";
import {
    MISSION_NODE_ID,
    normalizeGraph,
} from "./strategy-map-graph";
import {milestoneProgress} from "../../../models/project/milestone-progress";


const items = [
    // { id: "obj_1", type: "objective", title: "افزایش فروش", owner: "حلیمه", position: {x:0,y:0} },
    // { id: "prj_1", type: "project", title: "کمپین زمستان", owner: "حلیمه", progress: 35, parentId: "obj_1", parentIds: [], position: {x:0,y:220} },
    // { id: "tsk_1", type: "task", title: "نوشتن اسکریپت تماس", owner: "حلیمه", progress: 60, parentId: "prj_1", parentIds: [], position: {x:0,y:420} },
];

const MAX_ROOT_GOALS = 3;
const EMPTY_STRATEGY_FILTERS = {
    status: undefined,
    ownerIds: [],
};


function isMissionRootGoal(goalItem) {
    return !goalItem?.parentGoal?.id && !goalItem?.parentGoal?.documentId && !goalItem?.isOrphan;
}

function getMissionRootGoalsCount(goals = []) {
    return (goals || []).filter(isMissionRootGoal).length;
}

function StrategyMap(props) {
    const [data, setData] = useState(items);
    const translate = useTranslate()

    const goal = useGoal();
    const project = useProject();
    const workspace = useWorkspace();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const {user} = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const {workspaceMembership, workspaceRole} = useWorkspaceRole();
    const permission = usePermission();
    const canEditMission = permission.can("modify-workspace-settings");
    const canCreateGoal = permission.can("create-new-goals");
    const canCreateProject = permission.can("create-new-projects");
    const readOnly = Boolean(workspaceMembership)
        && (isWorkspaceReadOnlyRole(workspaceRole) || !workspaceRole);
    const [rawGoals, setRawGoals] = useState([]);
    const [rawProjects, setRawProjects] = useState([]);
    const [filterEditOpen, setFilterEditOpen] = useState(false);
    const [isOwnerOverlayOpen, setIsOwnerOverlayOpen] = useState(false);
    const [strategyFilters, setStrategyFilters] = useState(EMPTY_STRATEGY_FILTERS);
    const [draftStrategyFilters, setDraftStrategyFilters] = useState(EMPTY_STRATEGY_FILTERS);
    const filterEditPopoverRef = useRef(null);
    const [layoutRevision, setLayoutRevision] = useState(0);
    const [selectedCycle, setSelectedCycle] = useState({
        year: 1405,
        range: "",
    });

    const workspaceDocumentId =
        workspaceMembershipData?.workspace?.documentId ||
        workspaceMembershipData?.workspace?.id;

    const strategyMapApiConfig = {
        headers: {
            workspace: workspaceDocumentId,
        },
    };
    const [mission, setMission] = useState();
    const [missionDialogVisible, setMissionDialogVisible] = useState(false);
    const [strategyMapError, setStrategyMapError] = useState();
    const [strategyMapHydrated, setStrategyMapHydrated] = useState(false);
    const healthCounts = getStrategyHealthCounts(data);
    const visibleGoalIds = useMemo(
        () => data.filter((item) => item.type === "goal").map((item) => item.entity?.documentId || item.entity?.id).filter(Boolean),
        [data],
    );
    const visibleProjectIds = useMemo(
        () => data.filter((item) => item.type === "project").map((item) => item.entity?.documentId || item.entity?.id).filter(Boolean),
        [data],
    );
    usePublishAiScope({
        activeTab: "strategy-map",
        selectedEntityIds: visibleGoalIds,
        filters: {
            ...strategyFilters,
            selectedCycle,
            visibleGoalIds,
            visibleProjectIds,
            scopeSelectionProvided: true,
        },
    });
    const ownerOptions = useMemo(
        () => getStrategyOwnerOptions(rawGoals, rawProjects),
        [rawGoals, rawProjects]
    );
    const missionRootGoalsLimitReached = useMemo(
        () => getMissionRootGoalsCount(rawGoals) >= MAX_ROOT_GOALS,
        [rawGoals]
    );
    const hasTransientLayoutFilter = Boolean(
        selectedCycle?.range || getActiveStrategyFilterCount(strategyFilters)
    );

    const rebuildStrategyMap = useCallback(({
                                                goals = rawGoals,
                                                projects = rawProjects,
                                                nextCycle = selectedCycle,
                                                nextFilters = strategyFilters,
                                                nextMission = mission,
                                                shouldLayout = false,
                                            } = {}) => {
        if (!nextMission) return;

        const nextGoals = filterStrategyItems(
            goals,
            {
                timeRange: nextCycle?.range,
                selectedYear: nextCycle?.year,
                filters: nextFilters,
                statusField: "goalStatus",
            }
        );
        const nextProjects = filterStrategyItems(
            projects,
            {
                timeRange: nextCycle?.range,
                selectedYear: nextCycle?.year,
                filters: nextFilters,
                statusField: "projectStatus",
            }
        );

        setData(normalizeGraph(nextGoals, nextProjects, nextMission, {
            allGoals: goals,
            allProjects: projects,
        }));

        if (shouldLayout) {
            setLayoutRevision(revision => revision + 1);
        }
    }, [mission, rawGoals, rawProjects, selectedCycle, strategyFilters]);
    useEffect(() => {
        if (workspaceMembershipData){
            updateStrategyMap()
        }
    }, [workspaceMembershipData]);

    useEffect(() => {
        if (!filterEditOpen) return;

        const handleOutsidePointerDown = (event) => {
            const target = event.target;
            const filterPopover = filterEditPopoverRef.current;
            const isInsideFilterPopover = filterPopover?.contains(target);
            const isInsidePrimeOverlay = target?.closest?.(
                ".p-multiselect-panel, .p-dropdown-panel, .p-overlaypanel, .p-connected-overlay"
            );

            if (isInsideFilterPopover || isInsidePrimeOverlay) return;

            setIsOwnerOverlayOpen(false);
            setFilterEditOpen(false);
        };

        document.addEventListener("pointerdown", handleOutsidePointerDown, true);

        return () => {
            document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
        };
    }, [filterEditOpen]);

    useEffect(() => {
        if (!filterEditOpen) {
            setIsOwnerOverlayOpen(false);
        }
    }, [filterEditOpen]);


    // useUpdateEffect(() => {
    //     setData(normalizeGraph(workspaceMembershipData?.workspace?.goals, workspaceMembershipData?.workspace?.projects));
    // }, [workspaceMembershipData?.workspace]);

    useEffect(() => {
        if (!mission || !strategyMapHydrated) return;

        rebuildStrategyMap();
    }, [selectedCycle, strategyFilters, mission, strategyMapHydrated, rebuildStrategyMap]);

    const updateStrategyMap = async () => {
            try {
                setStrategyMapError(undefined);
                setStrategyMapHydrated(false);

                const nextMission =
                    workspaceMembershipData?.workspace?.mission ||
                    mission ||
                    workspaceMembershipData?.workspace?.name ||
                    translate("strategy-map.mission.default");

                setMission(nextMission);

                const [_goals, _projects] = await Promise.all([
                    goal.getGoals(workspaceMembershipData?.workspace?.id),
                    project.getProjects(workspaceMembershipData?.workspace?.id, true),
                ]);

                const scopedGoals = filterStrategyMapEntities(
                    _goals,
                    workspaceMembershipData?.workspace,
                );
                const scopedProjects = filterStrategyMapEntities(
                    _projects,
                    workspaceMembershipData?.workspace,
                );

                setRawGoals(scopedGoals);
                setRawProjects(scopedProjects);
                setStrategyMapHydrated(true);

                rebuildStrategyMap({
                    goals: scopedGoals,
                    projects: scopedProjects,
                    nextMission,
                });
            }
            catch (error) {
                setStrategyMapError(translate("strategy-map.load-error"));
            }
    };

    // const updateStrategyMap = () => {
    //
    //     (async () => {
    //         try {
    //             setStrategyMapError(undefined);
    //
    //             const [_goals, _projects, _strategyMap] = await Promise.all([
    //                 goal.getGoals(workspaceMembershipData?.workspace?.id),
    //                 project.getProjects(workspaceMembershipData?.workspace?.id, true),
    //                 workspaceDocumentId
    //                     ? api.get("/strategy-maps/active", strategyMapApiConfig).then(res => res.data).catch(() => undefined)
    //                     : undefined,                ]);
    //             setRawGoals(_goals || []);
    //             setRawProjects(_projects || []);
    //
    //             const nextMission =
    //                 _strategyMap?.data?.mission ||
    //                 _strategyMap?.mission ||
    //                 workspaceMembershipData?.workspace?.name ||
    //                 translate("strategy-map.mission.default");
    //
    //             setMission(nextMission);
    //             setData(normalizeGraph(
    //                 filterStrategyItemsByTimeRange(_goals, timeRange),
    //                 filterStrategyItemsByTimeRange(_projects, timeRange),
    //                 nextMission
    //             ));
    //         }
    //         catch (error) {
    //             setStrategyMapError(translate("strategy-map.load-error"));
    //         }
    //     })();
    // };

    const updateNodePosition = async (nodeId, position, entity, type, movedNodes = []) => {
        const updates = movedNodes.length
            ? movedNodes
            : [{ id: nodeId, position, data: { entity }, type }];

        await Promise.all(updates.map(async node => {
            const nodeEntity = node.data?.entity;

            if (!nodeEntity?.documentId) return;

            if (node.type === "project") {
                await project.updateProject(nodeEntity.documentId, {
                    mapX: node.position.x,
                    mapY: node.position.y,
                });
            }

            if (node.type === "goal") {
                await goal.updateGoal(nodeEntity.documentId, {
                    mapX: node.position.x,
                    mapY: node.position.y,
                });
            }
        }));

        setRawGoals(currentGoals => currentGoals.map(goalItem => {
            const movedNode = updates.find(node => node.type === "goal" && String(node.data?.entity?.id) === String(goalItem.id));

            if (!movedNode) return goalItem;

            return {
                ...goalItem,
                mapX: movedNode.position.x,
                mapY: movedNode.position.y,
            };
        }));
        setRawProjects(currentProjects => currentProjects.map(projectItem => {
            const movedNode = updates.find(node => node.type === "project" && String(node.data?.entity?.id) === String(projectItem.id));

            if (!movedNode) return projectItem;

            return {
                ...projectItem,
                mapX: movedNode.position.x,
                mapY: movedNode.position.y,
            };
        }));
    };

    const relationId = (entity) => entity?.documentId || entity?.id;
    const findGraphItem = (nodeId) => data.find(item => String(item.id) === String(nodeId));
    const graphItemRelationId = (nodeId) => relationId(findGraphItem(nodeId)?.entity);
    const showStrategyMapError = (error, fallbackKey = "strategy-map.load-error") => {
        const message = typeof error === "string"
            ? error
            : normalizeApiError(error)?.message;

        setStrategyMapError(translate(message || fallbackKey));
    };
    const refreshStrategyMapLayout = async () => {
        await updateStrategyMap();
        setLayoutRevision(revision => revision + 1);
    };
    const confirmStrategyMapAction = ({messageKey, acceptLabelKey = "delete", onAccept}) => {
        confirmDialog({
            className: "rtl",
            header: translate("confirm"),
            message: translate(messageKey),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate(acceptLabelKey),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs",
            rejectClassName: "p-button-text text-xs",
            defaultFocus: "reject",
            accept: onAccept,
        });
    };

    const connectNodes = async (connectionData) => {
        setStrategyMapError(undefined);
        try {

        const fromType = connectionData?.from?.node?.type;
        const toType = connectionData?.to?.node?.type;
        const fromNodeId = connectionData?.from?.node?.id;
        const toNodeId = connectionData?.to?.node?.id;
        const fromEdge = connectionData?.from?.edge;
        const fromEntity = connectionData?.from?.node?.data?.entity;
        const toEntity = connectionData?.to?.node?.data?.entity;

        if (fromNodeId && toNodeId && String(fromNodeId) === String(toNodeId)) {
            setStrategyMapError(translate("strategy-map.validation.self-connection"));
            return;
        }

        const hasGoalRelation = (projectData, goalData) => {
            const goalId = String(relationId(goalData));

            return (projectData?.goals || []).some(goalItem => (
                String(relationId(goalItem)) === goalId ||
                String(goalItem?.id) === String(goalData?.id)
            ));
        };

        if (fromType === "mission" || toType === "mission") {
            const otherSide = fromType === "mission" ? connectionData.to : connectionData.from;

            if (otherSide?.node?.type !== "goal") {
                setStrategyMapError(translate("strategy-map.validation.mission-project"));
                return;
            }

            const goalEntity = otherSide?.node?.data?.entity;
            const isAlreadyRoot = !goalEntity?.parentGoal?.id && !goalEntity?.parentGoal?.documentId && !goalEntity?.isOrphan;

            if (!isAlreadyRoot && missionRootGoalsLimitReached) {
                setStrategyMapError(translate("strategy-map.validation.max-root-goals"));
                return;
            }

            await goal.updateGoal(relationId(goalEntity), {
                parentGoal: null,
            }, {throwOnError: true});
            await refreshStrategyMapLayout();
            return;
        }

        if (fromType === "goal") {
            if (fromEdge === "top" && toType === "project") {
                setStrategyMapError(translate("strategy-map.validation.project-goal-only"));
                return;
            }

            if (fromEdge === "top" && toType === "goal") {
                await goal.updateGoal(relationId(fromEntity), {
                    parentGoal: relationId(toEntity),
                }, {throwOnError: true});
            }

            if (fromEdge === "bottom" && toType === "goal") {
                await goal.updateGoal(relationId(toEntity), {
                    parentGoal: relationId(fromEntity),
                }, {throwOnError: true});
            }

            if (fromEdge === "bottom" && toType === "project") {
                if (hasGoalRelation(toEntity, fromEntity)) {
                    setStrategyMapError(translate("strategy-map.validation.duplicate-connection"));
                    return;
                }

                await project.updateProject(relationId(toEntity), {
                    goals: {
                        connect: [relationId(fromEntity)],
                    },
                }, {throwOnError: true});
            }
        }

        if (fromType === "project") {
            if (fromEdge === "top" && toType === "project") {
                const parentId = relationId(toEntity);
                const currentParentIds = (fromEntity.parentProjects || []).map(relationId).filter(Boolean);

                if (currentParentIds.length && !currentParentIds.some(item => String(item) === String(parentId))) {
                    setStrategyMapError(translate("strategy-map.validation.project-single-parent"));
                    return;
                }

                await project.updateProject(relationId(fromEntity), {
                    parentProjects: {
                        set: [parentId],
                    },
                }, {throwOnError: true});

                await refreshStrategyMapLayout();
                return;
            }

            if (toType !== "goal" || fromEdge !== "top") {
                setStrategyMapError(translate("strategy-map.validation.project-goal-only"));
                return;
            }

            if (hasGoalRelation(fromEntity, toEntity)) {
                setStrategyMapError(translate("strategy-map.validation.duplicate-connection"));
                return;
            }

            await project.updateProject(relationId(fromEntity), {
                goals: {
                    connect: [relationId(toEntity)],
                },
            }, {throwOnError: true});
        }

        await refreshStrategyMapLayout();
        }
        catch (error) {
            showStrategyMapError(error);
        }
    };
    const [reparentGoalData, setReparentGoalData] = useState();
    const openGoalReparent = (goalData) => {
        if (!goalData) return;
        setReparentGoalData(goalData);
    };
    // choice: MISSION_PARENT (attach to the mission), a goal id (child of that goal) or null (no parent).
    const confirmGoalReparent = async (goalData, choice) => {
        try {
            const placement = choice === MISSION_PARENT
                ? {parentGoal: null, isOrphan: false}
                : choice ? {parentGoal: choice, isOrphan: false} : {parentGoal: null, isOrphan: true};
            await goal.updateGoal(relationId(goalData), placement, {throwOnError: true});
            setReparentGoalData(undefined);
            await refreshStrategyMapLayout();
        }
        catch (error) {
            setReparentGoalData(undefined);
            showStrategyMapError(error);
        }
    };
    // goal -> goal and mission -> goal edges cannot be removed, only re-attached to another parent
    const isGoalTargetEdge = (edge) => {
        const sourceType = findGraphItem(edge?.source)?.type;
        const targetType = findGraphItem(edge?.target)?.type;
        return targetType === "goal" && (sourceType === "goal" || sourceType === "mission");
    };
    const disconnectEdge = async (edge) => {
        const sourceItem = findGraphItem(edge.source);
        const targetItem = findGraphItem(edge.target);
        const sourceType = sourceItem?.type;
        const targetType = targetItem?.type;
        const sourceRelationId = graphItemRelationId(edge.source);
        const targetRelationId = graphItemRelationId(edge.target);

        if (!sourceItem || !targetItem || !sourceRelationId || !targetRelationId) {
            showStrategyMapError("strategy-map.validation.invalid-connection");
            return;
        }

        if (targetType === "goal" && (sourceType === "mission" || sourceType === "goal")) {
            openGoalReparent(targetItem.entity);
            return;
        }

        if (sourceType === "goal" && targetType === "project") {
            await project.updateProject(targetRelationId, {
                goals: {
                    disconnect: [sourceRelationId],
                },
            }, {throwOnError: true});
        }

        if (sourceType === "project" && targetType === "project") {
            await project.updateProject(targetRelationId, {
                parentProjects: {
                    disconnect: [sourceRelationId],
                },
            }, {throwOnError: true});
        }

        await refreshStrategyMapLayout();
    };
    const disconnectNodes = async (edges = []) => {
        try {
            const goalEdges = (edges || []).filter(isGoalTargetEdge);
            if (goalEdges.length) openGoalReparent(findGraphItem(goalEdges[0].target)?.entity);
            await Promise.all((edges || []).filter((edge) => !isGoalTargetEdge(edge)).map(disconnectEdge));
        }
        catch (error) {
            showStrategyMapError(error);
        }
    };
    const confirmDisconnectEdge = (edge) => {
        if (isGoalTargetEdge(edge)) {
            openGoalReparent(findGraphItem(edge.target)?.entity);
            return;
        }
        confirmStrategyMapAction({
            messageKey: "strategy-map.confirm.disconnect",
            acceptLabelKey: "strategy-map.action.disconnect",
            onAccept: () => disconnectNodes([edge]),
        });
    };
    const disconnectProjectParent = async (projectData) => {
        try {
            await project.updateProject(relationId(projectData), {
                parentProjects: {
                    set: [],
                },
            }, {throwOnError: true});
            await refreshStrategyMapLayout();
        }
        catch (error) {
            showStrategyMapError(error);
        }
    };
    const disconnectProjectGoals = async (projectData) => {
        try {
            await project.updateProject(relationId(projectData), {
                goals: {
                    set: [],
                },
            }, {throwOnError: true});
            await refreshStrategyMapLayout();
        }
        catch (error) {
            showStrategyMapError(error);
        }
    };
    const archiveProject = async (projectData)=>{
        await project.updateProject(projectData.documentId, {archivedAt:new Date(), archivedBy:workspaceMembershipData}, {throwOnError: true})
        await refreshStrategyMapLayout();
    }
    const archiveGoal = async (goalData)=>{
        await goal.updateGoal(goalData.documentId, {archivedAt:new Date(), archivedBy:workspaceMembershipData}, {throwOnError: true})
        await refreshStrategyMapLayout();
    }
    const confirmArchiveProject = (projectData) => {
        confirmStrategyMapAction({
            messageKey: "strategy-map.confirm.archive-project",
            acceptLabelKey: "archive-project",
            onAccept: async () => {
                try {
                    await archiveProject(projectData);
                }
                catch (error) {
                    showStrategyMapError(error);
                }
            },
        });
    };
    const confirmArchiveGoal = (goalData) => {
        confirmStrategyMapAction({
            messageKey: "strategy-map.confirm.archive-goal",
            acceptLabelKey: "archive-goal",
            onAccept: async () => {
                try {
                    await archiveGoal(goalData);
                }
                catch (error) {
                    showStrategyMapError(error);
                }
            },
        });
    };

    const openAddChildDialog = (entity, type) => {
        if (type === "mission" && missionRootGoalsLimitReached) {
            setStrategyMapError(translate("strategy-map.validation.max-root-goals"));
            return;
        }

        props.setPending?.({
            mode: "create-child",
            from: {
                node: {
                    id: type === "mission" ? MISSION_NODE_ID : `${type}_${entity.id}`,
                    type,
                    data: {entity},
                },
                edge: "bottom",
            },
            position: {
                x: Number(entity.mapX || 300) + 120,
                y: Number(entity.mapY || 120) + 220,
            },
            draft: {
                type: type === "project" ? "project" : "goal",
            },
        });

        props.setGoalDialogVisible?.(true);
    };

    const openAddParentDialog = (entity, type) => {
        props.setPending?.({
            mode: "create-parent",
            from: {
                node: {
                    id: `${type}_${entity.id}`,
                    type,
                    data: {entity},
                },
                edge: "top",
            },
            position: {
                x: Number(entity.mapX || 300) - 120,
                y: Number(entity.mapY || 120) - 220,
            },
            draft: {
                type: type === "goal" ? "goal" : "project",
            },
        });

        props.setGoalDialogVisible?.(true);
    };

    const nodeTypes = {
        mission: (p) => (
            <BaseNode
                {...p}
                type="mission"
                template={MissionNode}
                onEditClick={() => setMissionDialogVisible(true)}
                onAddChild={openAddChildDialog}
                disableAddChild={missionRootGoalsLimitReached || !canCreateGoal}
                templateReadOnly={readOnly || !canEditMission}
                readOnly={readOnly}
            />
        ),
        goal: (p) => (
            <BaseNode
                {...p}
                type="goal"
                template={GoalNode}
                onDeleteClick={(e) => confirmArchiveGoal(e)}
                onDisconnectParent={(e) => openGoalReparent(e)}
                onAddParent={openAddParentDialog}
                onAddChild={openAddChildDialog}
                disableAddParent={!canCreateGoal && !canCreateProject}
                disableAddChild={!canCreateGoal && !canCreateProject}
                readOnly={readOnly}
            />
        ),
        project: (p) => (
            <BaseNode
                {...p}
                type="project"
                template={ProjectNode}
                onDeleteClick={(e) => confirmArchiveProject(e)}
                onDisconnectParent={(e) => {
                    confirmStrategyMapAction({
                        messageKey: "strategy-map.confirm.disconnect",
                        acceptLabelKey: "strategy-map.action.disconnect",
                        onAccept: () => disconnectProjectParent(e),
                    });
                }}
                onDisconnectGoals={(e) => {
                    confirmStrategyMapAction({
                        messageKey: "strategy-map.confirm.disconnect",
                        acceptLabelKey: "strategy-map.action.disconnect",
                        onAccept: () => disconnectProjectGoals(e),
                    });
                }}
                onAddParent={openAddParentDialog}
                onAddChild={openAddChildDialog}
                disableAddParent={!canCreateGoal && !canCreateProject}
                disableAddChild={!canCreateProject}
                readOnly={readOnly}
            />
        ),
        orphanSection: (p) => <OrphanProjectsSectionNode {...p.data}/>,
    };

    // const nodeTypes = {
    //     mission: (p) => (
    //         <BaseNode
    //             {...p}
    //             type="mission"
    //             template={MissionNode}
    //             onEditClick={() => setMissionDialogVisible(true)}
    //         />
    //     ),        goal: (p) => <BaseNode {...p} type="goal" template={GoalNode} onDeleteClick={(e)=>{archiveGoal(e)}} />,
    //     project: (p) => <BaseNode {...p} type="project" template={ProjectNode} onDeleteClick={(e)=>{archiveProject(e)}}/>,
    //     // task: (p) => <BaseNode {...p} type="task" template={}/>,
    // };

    const graphNodeCallback = (event) => {
        if (event?.mode === "edit" && event?.type === "orphanSection") {
            return;
        }

        if (event?.mode === "edit" && ["goal", "project"].includes(event?.type)) {
            const entity = event?.data?.entity;
            if (!canEnterStrategyMapEntity(entity, workspaceMembershipData)) {
                dispatch(setMessage({severity: "warn", content: translate("no-permission")}));
                return;
            }
        }

        if (event?.mode === "edit" && event?.type === "mission") {
            if (readOnly || !permission.can("modify-workspace-settings", true)) {
                dispatch(setMessage({severity: "warn", content: translate("no-permission")}));
                return;
            }
            setMissionDialogVisible(true);
            return;
        }

        if (event?.mode === "create-child" && event?.from?.node?.type === "mission") {
            if (missionRootGoalsLimitReached) {
                setStrategyMapError(translate("strategy-map.validation.max-root-goals"));
                return;
            }

            props.setPending?.({
                ...event,
                draft: {
                    ...(event.draft || {}),
                    type: "goal",
                },
            });

            props.setGoalDialogVisible?.(true);
            return;
        }

        props.setPending?.(event);
        props.setGoalDialogVisible?.(event.mode !== "edit");
    };

    const saveMission = async (nextMission) => {
        try {
            if (!permission.can("modify-workspace-settings", true)) return;
            if (!workspaceDocumentId) {
                setStrategyMapError(translate("strategy-map.load-error"));
                return;
            }

            await workspace.updateWorkspace(workspaceDocumentId, {
                mission: nextMission,
            });

            setMission(nextMission);

            setData(prevItems => prevItems.map(item => {
                if (item.id !== MISSION_NODE_ID) return item;

                return {
                    ...item,
                    entity: {
                        ...item.entity,
                        title: nextMission,
                    },
                };
            }));

            setMissionDialogVisible(false);
            setStrategyMapError(undefined);
        }
        catch (error) {
            setStrategyMapError(translate("strategy-map.load-error"));
        }
    };

    // const saveMission = async (nextMission) => {
    //     try {
    //         if (!workspaceDocumentId) {
    //             setStrategyMapError(translate("strategy-map.load-error"));
    //             return;
    //         }
    //
    //         await api.put(
    //             "/strategy-maps/mission",
    //             {
    //                 mission: nextMission,
    //                 workspace: workspaceDocumentId,
    //             },
    //             strategyMapApiConfig
    //         );
    //
    //         setMission(nextMission);
    //         setData(prevItems => prevItems.map(item => {
    //             if (item.id !== MISSION_NODE_ID) return item;
    //
    //             return {
    //                 ...item,
    //                 entity: {
    //                     ...item.entity,
    //                     title: nextMission,
    //                 },
    //             };
    //         }));
    //
    //         setMissionDialogVisible(false);
    //         setStrategyMapError(undefined);
    //     }
    //     catch (error) {
    //         setStrategyMapError(translate("strategy-map.load-error"));
    //     }
    // };


    return (
        <div className="h-full flex flex-column overflow-hidden pb-3">
            <PageHeader title={props.name} description={props.description}/>

            {strategyMapError && (
                <Message severity="warn" text={strategyMapError} className="mx-3 mb-2"/>
            )}

            <div className="relative flex-1 min-h-0 flex flex-column">
                <StrategyMapToolbar
                    readOnly={readOnly}
                    selectedCycle={selectedCycle}
                    onCycleChange={setSelectedCycle}
                    filterEditOpen={filterEditOpen}
                    setFilterEditOpen={setFilterEditOpen}
                    activeFiltersCount={getActiveStrategyFilterCount(strategyFilters)}
                    filters={strategyFilters}
                    ownerOptions={ownerOptions}
                    setDraftStrategyFilters={setDraftStrategyFilters}
                    rootGoalsLimitReached={missionRootGoalsLimitReached}
                    onClearCycle={() => {
                        const nextCycle = {
                            ...selectedCycle,
                            range: "",
                        };

                        setSelectedCycle(nextCycle);
                        rebuildStrategyMap({nextCycle});
                    }}
                    onClearFilters={() => {
                        setDraftStrategyFilters(EMPTY_STRATEGY_FILTERS);
                        setStrategyFilters(EMPTY_STRATEGY_FILTERS);
                        rebuildStrategyMap({nextFilters: EMPTY_STRATEGY_FILTERS});
                        setFilterEditOpen(false);
                    }}
                    onCreateGoal={() => {
                        if (missionRootGoalsLimitReached) {
                            setStrategyMapError(translate("strategy-map.validation.max-root-goals"));
                            return;
                        }

                        props.setPending?.({
                            mode: "create-root",
                            position: { x: 160, y: 160 },
                            draft: { type: "goal" },
                        });
                        props.setGoalDialogVisible?.(true);
                    }}
                />

                {filterEditOpen && (
                    <FilterEditPopover
                        filters={draftStrategyFilters}
                        setFilters={setDraftStrategyFilters}
                        ownerOptions={ownerOptions}
                        popoverRef={filterEditPopoverRef}
                        isOwnerOverlayOpen={isOwnerOverlayOpen}
                        setIsOwnerOverlayOpen={setIsOwnerOverlayOpen}
                        onApply={(nextFilters) => {
                            setStrategyFilters(nextFilters);
                            rebuildStrategyMap({
                                nextFilters,
                            });
                            setFilterEditOpen(false);
                        }}
                        onReset={() => {
                            setDraftStrategyFilters(EMPTY_STRATEGY_FILTERS);
                            setStrategyFilters(EMPTY_STRATEGY_FILTERS);
                            rebuildStrategyMap({
                                nextFilters: EMPTY_STRATEGY_FILTERS,
                            });
                            setFilterEditOpen(false);
                        }}
                        onCancel={() => setFilterEditOpen(false)}
                    />
                )}


                <div className="flex-1 min-h-0 relative px-3 pb-3">
                    <div className="w-full h-full overflow-hidden" style={{direction: "ltr"}}>
                        <ZofinoGraph
                            readOnly={readOnly}
                            lockNodeDrag
                            onEdgesDelete={(e) => {
                                disconnectNodes(e)
                            }}
                            onEdgeAction={({edge}) => confirmDisconnectEdge(edge)}
                            nodeTypes={nodeTypes}
                            items={data}
                            layoutRevision={layoutRevision}
                            layoutMode={hasTransientLayoutFilter ? "filtered" : "base"}
                            onRequestDialog={graphNodeCallback}
                            onChange={(e) => {
                                connectNodes(e)
                            }}
                            onNodePositionChange={updateNodePosition}
                        />
                    </div>

                    <aside
                        className="absolute right-0 bottom-0 m-3"
                        style={{
                            width: "16rem",
                            direction: "rtl",
                            zIndex: 4,
                        }}
                    >
                        <StrategyHealthLegend counts={healthCounts}/>
                    </aside>
                </div>
            </div>
            <MissionDialog
                visible={!readOnly && canEditMission && missionDialogVisible}
                mission={mission}
                onHide={() => setMissionDialogVisible(false)}
                onSubmit={saveMission}
            />
            <GoalReparentDialog
                visible={Boolean(reparentGoalData)}
                goalData={reparentGoalData}
                goals={rawGoals}
                onHide={() => setReparentGoalData(undefined)}
                onConfirm={confirmGoalReparent}
            />
            <ConfirmDialog className="rtl"/>
        </div>
    );
}

function filterStrategyItems(items, {timeRange, selectedYear, filters, statusField}) {
    return filterStrategyItemsByOwnerAndStatus(
        filterStrategyItemsByTimeRange(items, timeRange, selectedYear),
        filters,
        statusField
    );
}

function filterStrategyItemsByOwnerAndStatus(items, filters, statusField) {
    const status = filters?.status;
    const ownerIds = filters?.ownerIds || [];

    return (items || []).filter(item => {
        if (status && item?.[statusField] !== status) {
            return false;
        }

        if (ownerIds.length && !ownerIds.includes(getEntityOwnerId(item))) {
            return false;
        }

        return true;
    });
}

function filterStrategyItemsByTimeRange(items, timeRange, selectedYear) {
    if (!timeRange || timeRange === "all") return items || [];

    return (items || []).filter(item => {
        const itemCycle = item.cycle;

        if (typeof itemCycle === "string") {
            return timeRangeMatchesCycle(timeRange, itemCycle);
        }

        if (itemCycle?.range) {
            if (selectedYear && itemCycle.year && Number(itemCycle.year) !== Number(selectedYear)) {
                return false;
            }

            return timeRangeMatchesCycle(timeRange, itemCycle.range);
        }

        const dateValue =
            item.dueDate ||
            item.deliverAt ||
            item.endDate ||
            item.deadline ||
            item.createdAt;

        const persianDate = getPersianDateParts(dateValue);

        if (!persianDate) return false;

        if (selectedYear && persianDate.year !== Number(selectedYear)) {
            return false;
        }

        const month = persianDate.month;

        if (timeRange === "Q1") return month >= 1 && month <= 3;
        if (timeRange === "Q2") return month >= 4 && month <= 6;
        if (timeRange === "Q3") return month >= 7 && month <= 9;
        if (timeRange === "Q4") return month >= 10 && month <= 12;

        if (timeRange === "H1") return month >= 1 && month <= 6;
        if (timeRange === "H2") return month >= 7 && month <= 12;

        if (timeRange === "YEAR") return true;

        return true;
    });
}


function timeRangeMatchesCycle(selectedRange, itemRange) {
    if (!selectedRange || selectedRange === "all") return true;
    if (!itemRange) return false;

    return selectedRange === itemRange;
}

function getEntityOwnerId(entity) {
    return String(entity?.owner?.documentId || entity?.owner?.id || "");
}

function getStrategyOwnerOptions(goals, projects) {
    const ownersById = {};

    [...(goals || []), ...(projects || [])].forEach(item => {
        const ownerId = getEntityOwnerId(item);

        if (!ownerId || ownersById[ownerId]) return;

        ownersById[ownerId] = {
            label: item.owner?.member?.fullname || item.owner?.member?.username || item.owner?.member?.email || ownerId,
            value: ownerId,
            owner: item.owner,
        };
    });

    return Object.values(ownersById);
}

function getActiveStrategyFilterCount(filters) {
    return (filters?.status ? 1 : 0) + (filters?.ownerIds?.length || 0);
}

function getPersianDateParts(value) {
    if (!value) return undefined;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return undefined;

    const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
    }).formatToParts(date);

    const year = normalizePersianNumber(parts.find(part => part.type === "year")?.value);
    const month = normalizePersianNumber(parts.find(part => part.type === "month")?.value);

    if (!year || !month) return undefined;

    return {
        year,
        month,
    };
}


function normalizePersianNumber(value) {
    if (!value) return undefined;

    const normalized = String(value)
        .replace(/[۰-۹]/g, digit => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit))
        .replace(/[٠-٩]/g, digit => "٠١٢٣٤٥٦٧٨٩".indexOf(digit));

    const numberValue = Number(normalized);

    return Number.isFinite(numberValue) ? numberValue : undefined;
}
function getGregorianMonth(value) {
    if (!value) return undefined;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return undefined;

    return date.getMonth();
}

function StrategyHealthLegend({counts}) {
    const translate = useTranslate();

    const items = [
        {
            key: "on-way",
            label: translate("on-way"),
            count: counts.onWay,
            color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.ON_TRACK],
            bold: false,
        },
        {
            key: "has-risks",
            label: translate("has-risks"),
            count: counts.hasRisks,
            color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.AT_RISK],
            bold: true,
        },
        {
            key: "out-of-way",
            label: translate("out-of-way"),
            count: counts.outOfWay,
            color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.OFF_TRACK],
            bold: false,
        },
    ];

    return (
        <div
            className="bg-white shadow-4 border-1 border-round-2xl p-4 rtl text-xs"
            style={{
                width: "16rem",
                borderColor: "#e3bfb4",
            }}
        >
            <div className="flex align-items-center gap-2 font-bold mb-3 text-900">
                <Icon icon="mi mi:analytics" className="text-lg"/>
                <span>{translate("strategy-map.health-legend")}</span>
            </div>

            <div className="flex flex-column gap-3">
                {items.map(item => (
                    <div key={item.key} className="flex align-items-center justify-content-between gap-3">
                        <div className="flex align-items-center gap-2">
                            <span
                                className="inline-block border-circle"
                                style={{
                                    width: 12,
                                    height: 12,
                                    background: item.color,
                                    boxShadow: item.key === "has-risks" ? `0 0 8px ${withAlpha(item.color, 0.4)}` : undefined,
                                }}
                            />
                            <span
                                style={{
                                    color: item.bold ? item.color : "#191c1e",
                                    fontWeight: item.bold ? 700 : 400,
                                }}
                            >
                                {item.label}
                            </span>
                        </div>

                        <span
                            style={{
                                color: item.bold ? item.color : "#5a4139",
                                fontWeight: item.bold ? 700 : 400,
                            }}
                        >
                            {item.count} مورد
                        </span>
                    </div>
                ))}
            </div>

            <div
                className="mt-4 pt-3 text-2xs line-height-3"
                style={{
                    borderTop: "1px solid rgba(227, 191, 180, 0.45)",
                    color: "#5a4139",
                }}
            >
                {counts.outOfWay > 0
                    ? "گره هدف یا پروژه به دلیل وجود مورد خارج از مسیر، وضعیت بحرانی نشان می‌دهد."
                    : counts.hasRisks > 0
                        ? "گره هدف یا پروژه به دلیل وجود مورد در ریسک، وضعیت هشدار نشان می‌دهد."
                        : counts.onWay > 0
                            ? "گره هدف یا پروژه‌ها در مسیر پیشرفت هستند."
                            : "هنوز موردی برای سنجش سلامت استراتژی وجود ندارد."
                }
            </div>
        </div>
    );
}
function getStrategyHealthCounts(items) {
    return (items || []).reduce((acc, item) => {
        if (item.type !== "project") return acc;

        const status = normalizeStrategyStatus(item.entity?.projectStatus);

        if (status === STRATEGY_STATUS.ON_TRACK) {
            return {...acc, onWay: acc.onWay + 1};
        }

        if (status === STRATEGY_STATUS.AT_RISK) {
            return {...acc, hasRisks: acc.hasRisks + 1};
        }

        if (status === STRATEGY_STATUS.OFF_TRACK) {
            return {...acc, outOfWay: acc.outOfWay + 1};
        }

        return acc;
    }, {
        onWay: 0,
        hasRisks: 0,
        outOfWay: 0,
    });
}

function getTimeRangeLabel(value) {
    switch (value) {
        case "Q1":
            return "سه ماهه اول";
        case "Q2":
            return "سه ماهه دوم";
        case "Q3":
            return "سه ماهه سوم";
        case "Q4":
            return "سه ماهه چهارم";
        case "H1":
            return "شش ماهه اول";
        case "H2":
            return "شش ماهه دوم";
        case "YEAR":
            return "سالانه";
        case "all":
        default:
            return "همه";
    }
}

function StrategyMapToolbar({
                                selectedCycle, onCycleChange, onCreateGoal,
                                filterEditOpen, setFilterEditOpen, activeFiltersCount,
                                filters, ownerOptions, setDraftStrategyFilters, onClearFilters, onClearCycle,
                                rootGoalsLimitReached,
                                readOnly,
                            }) {
    const translate = useTranslate();
    const cycleOverlayRef = useRef();
    const hasCycleFilter = Boolean(selectedCycle?.range);
    const hasAnyFilter = hasCycleFilter || activeFiltersCount > 0;
    const filterControlColor = hasAnyFilter
        ? "var(--secondary-color)"
        : "var(--primary-color)";
    const selectedStatus = getStrategyStatusOptions(translate)
        .find(option => option.value === filters?.status);
    const selectedOwners = (filters?.ownerIds || [])
        .map(id => ownerOptions?.find(option => option.value === id))
        .filter(Boolean);

    return (
        <div
            className="absolute z-5 bg-white border-1 shadow-3 rtl flex  align-items-center"
            style={{
                top: "1.5rem",
                left: "50%",
                transform: "translateX(-50%)",
                height: "3.5rem",
                width: "fit-content",
                whiteSpace: "nowrap",
                borderColor: "#ead5ce",
                borderRadius: "1.25rem",
                padding: "0 0.75rem",
                gap: "0.4rem"
            }}
            onClick={(event) => event.stopPropagation()}
        >
	            {!readOnly && (
	                <>
	                    <Button icon={<Icon icon="mi mi:add" className="text-xl"/>} label={translate("create-goal")}
	                            className="text-xs flex gap-2 p-button-rounded p-button-sm shadow-none border-none p-2"
	                            disabled={rootGoalsLimitReached}
	                            tooltip={rootGoalsLimitReached ? translate("strategy-map.validation.max-root-goals") : undefined}
	                            tooltipOptions={{position: "bottom"}}
	                            onClick={onCreateGoal}/>

                    <Divider layout="vertical" className="m-0 h-2rem"/>
                </>
            )}

            <div className="flex align-items-center" onClick={(event) => event.stopPropagation()}>
                <Button type="button" icon={<Icon icon="mi mi:calendar_today" className="text-lg" style={{color: filterControlColor}}/>}
                        className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 shadow-none border-none text-xs px-3 py-2"
                        style={{color: filterControlColor}}
                        label={selectedCycle?.range ? `${translate(selectedCycle.range)} ${selectedCycle.year}` : translate("select-cycle")}
                        onClick={(event) => cycleOverlayRef.current.toggle(event)}/>
                <OverlayPanel ref={cycleOverlayRef}
                              dismissable={true}
                              pt={{root: () => ({className: "p-0 rtl border-round-xl"}), content: () => ({className: "p-0"})}}>
                    <SelectCycle value={selectedCycle} onChange={onCycleChange}
                                 onHide={() => cycleOverlayRef.current.hide()}/>
                </OverlayPanel>
            </div>

            <div className="flex align-items-center gap-1 ">
                {selectedStatus && (
                    <span className="flex align-items-center gap-1 border-round-2xl px-2 py-1 text-xs surface-100">
                        <span className="border-circle" style={{width: 8, height: 8, background: selectedStatus.color}}/>
                        {selectedStatus.label}
                    </span>
                )}
                {selectedOwners.map(owner => (
                    <span key={owner.value} className="flex align-items-center gap-1 border-round-2xl  px-2 py-1 text-xs surface-100">
                        <Avatar memberData={owner.owner?.member} size="small"/>{owner.label}
                    </span>
                ))}
            </div>

            <Button type="button" icon={<Icon icon="mi mi:edit" className="text-xl" style={{color: filterControlColor}}/>}
                    className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 shadow-none border-none w-2rem h-2rem p-0"
                    style={{color: filterControlColor}}
                    onClick={() => {
                        setDraftStrategyFilters?.({status: filters?.status, ownerIds: [...(filters?.ownerIds || [])]});
                        setFilterEditOpen(!filterEditOpen);
                    }}/>

            {hasAnyFilter && (
                <Button type="button" icon={<Icon icon="mi mi:filter_alt_off" className="text-lg"/>}
                        className="p-button-rounded p-button-secondary p-button-text surface-100 active:surface-200 shadow-none border-none w-2rem h-2rem p-0"
                        tooltip={translate("clear-filters")} tooltipOptions={{position: "bottom"}}
                        onClick={(event) => {
                            event.stopPropagation();
                            if (hasCycleFilter) {
                                onClearCycle?.();
                            }

                            if (activeFiltersCount > 0) {
                                onClearFilters?.();
                            }
                        }}/>
            )}
        </div>
    );
}

// function StrategyMapToolbar({timeRange, onTimeRangeChange, onCreateGoal, timePeriodOpen, setTimePeriodOpen, filterEditOpen, setFilterEditOpen, selectedYear, setSelectedYear, selectedTimeRange, setSelectedTimeRange,}) {
//     const translate = useTranslate();
//     const selectedTimeRangeLabel = getTimeRangeLabel(selectedTimeRange || timeRange);
//
//     return (
//         <div
//             className="absolute z-5 bg-white border-round-3xl shadow-4 border-1 border-50 px-2 py-1 rtl flex align-items-center gap-2"
//             style={{
//                 left: "50%",
//                 top: "1rem",
//                 transform: "translateX(-50%)",
//                 height: "52px",
//             }}
//         >
//             <Button
//                 label={translate("create-goal")}
//                 className="bg-primary border-none border-round-2xl px-4 py-2 text-sm"
//                 onClick={onCreateGoal}
//             />
//
//             <Divider layout="vertical" className="mx-1" />
//
//             <div
//                 className="flex align-items-center gap-3 px-4 py-2 border-round-xl cursor-pointer hover:surface-50 transition-colors"
//                 onClick={(e) => {
//                     e.stopPropagation();
//                     setTimePeriodOpen(!timePeriodOpen);
//                     setFilterEditOpen(false);
//                 }}
//             >
//                 <Icon icon="mi mi:calendar_today" className="text-xl text-500" />
//
//                 <span className="text-sm text-500">
//                     {translate("time-range")}:
//                     <b className="text-900 mr-1">{selectedTimeRangeLabel}</b>
//                 </span>
//
//                 <Icon
//                     icon="mi mi:edit"
//                     className="text-lg text-400 hover:text-primary transition-colors"
//                     onClick={(e) => {
//                         e.stopPropagation();
//                         setFilterEditOpen(!filterEditOpen);
//                         setTimePeriodOpen(false);
//                     }}
//                 />
//             </div>
//         </div>
//     );
// }
function MissionDialog({visible, mission, onHide, onSubmit}) {
    const translate = useTranslate();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            mission: mission || "",
        },
        validate: (values) => {
            const errors = {};

            if (!values.mission?.trim()) {
                errors.mission = translate("strategy-map.mission.required");
            }

            return errors;
        },
        onSubmit: (values) => {
            onSubmit?.(values.mission.trim());
        },
    });

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            modal
            draggable={false}
            resizable={false}
            header={translate("strategy-map.mission.details")}
            className="w-11 md:w-7 lg:w-6 rtl"
            pt={{
                root: {className: "border-round-2xl"},
                header: {className: "p-3 text-sm border-bottom-1 border-200 border-round-top-2xl"},
                content: {className: "p-3"},
                footer: {className: "p-0 border-round-bottom-2xl"},
            }}
            footer={(
                <div className="flex align-items-center justify-content-end gap-2 w-full p-3 border-top-1 border-200">
                    <Button type="button" text severity="secondary" label={translate("cancel")} className="text-xs" onClick={onHide}/>
                    <Button type="button" icon="pi pi-check" label={translate("strategy-map.mission.save")} className="text-xs gap-2" onClick={formik.handleSubmit}/>
                </div>
            )}
        >
            <div className="flex flex-column gap-3">
                    <label className="block font-bold text-xs">
                        {translate("strategy-map.mission.title-label")}
                    </label>

                    <InputTextarea
                        name="mission"
                        value={formik.values.mission}
                        onChange={formik.handleChange}
                        rows={5}
                        autoResize
                        className="w-full text-xs"
                        placeholder={translate("strategy-map.mission.placeholder")}
                    />

                    {formik.errors.mission && (
                        <small className="block mt-2 text-red-500">
                            {formik.errors.mission}
                        </small>
                    )}

                    <div className="flex align-items-start gap-2 text-xs text-600">
                        <Icon icon="mi mi:info" className="text-base"/>
                        <span>
                            {translate("strategy-map.mission.visibility-note")}
                        </span>
                    </div>
            </div>
        </Dialog>
    );
}


// function TimePeriodPopover({selectedYear, setSelectedYear, selectedTimeRange, setSelectedTimeRange, onClose,}) {
//     const options = [
//         { label: "سه ماهه اول", value: "Q1" },
//         { label: "سه ماهه دوم", value: "Q2" },
//         { label: "سه ماهه سوم", value: "Q3" },
//         { label: "سه ماهه چهارم", value: "Q4" },
//         { label: "شش ماهه اول", value: "H1" },
//         { label: "شش ماهه دوم", value: "H2" },
//         { label: "سالانه", value: "YEAR" },
//     ];
//
//     return (
//         <div
//             className="fixed z-5 bg-white border-1 border-200 border-round-2xl shadow-4 p-4"
//             style={{
//                 top: 88,
//                 left: "50%",
//                 transform: "translateX(-100%)",
//                 width: 400,
//             }}
//             onClick={(e) => e.stopPropagation()}
//         >
//             <div className="flex align-items-center justify-content-between mb-4">
//                 <button
//                     type="button"
//                     className="border-none bg-transparent cursor-pointer"
//                     onClick={() => setSelectedYear((y) => y + 1)}
//                 >
//                     <Icon icon="mi mi:chevron_right" className="text-xl" />
//                 </button>
//
//                 <span className="font-bold text-lg">{selectedYear}</span>
//
//                 <button
//                     type="button"
//                     className="border-none bg-transparent cursor-pointer"
//                     onClick={() => setSelectedYear((y) => y - 1)}
//                 >
//                     <Icon icon="mi mi:chevron_left" className="text-xl" />
//                 </button>
//             </div>
//
//             <div className="grid">
//                 {options.map((item) => {
//                     const active = selectedTimeRange === item.value;
//
//                     return (
//                         <div
//                             key={item.value}
//                             className={item.value === "YEAR" ? "col-12" : "col-6"}
//                         >
//                             <button
//                                 type="button"
//                                 className={`w-full border-1 border-round-lg py-2 px-3 text-sm cursor-pointer transition-colors ${
//                                     active
//                                         ? "bg-primary text-white border-primary"
//                                         : "bg-white text-700 border-300 hover:surface-100"
//                                 }`}
//                                 onClick={() => setSelectedTimeRange(item.value)}
//                             >
//                                 {item.label}
//                             </button>
//                         </div>
//                     );
//                 })}
//             </div>
//         </div>
//     );
// }

// eslint-disable-next-line no-unused-vars
function LegacyFilterEditPopover({ selectedTimeRange, onApply, onCancel }) {
    return (
        <div
            className="fixed z-5 bg-white border-1 border-200 border-round-2xl shadow-4 p-4"
            style={{
                top: 88,
                left: "50%",
                transform: "translateX(-10%)",
                width: 320,
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="mb-4 font-bold">تنظیمات فیلتر</div>

            <div className="text-sm text-600 mb-4">
                بازه انتخاب‌شده: <b>{getTimeRangeLabel(selectedTimeRange)}</b>
            </div>

            <div className="flex justify-content-end gap-2">
                <Button
                    type="button"
                    label="انصراف"
                    text
                    onClick={onCancel}
                />
                <Button
                    type="button"
                    label="اعمال فیلتر"
                    onClick={onApply}
                />
            </div>
        </div>
    );
}

function getStrategyStatusOptions(translate) {
    return [
        {label: translate("on-way"), value: "on-way", color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.ON_TRACK]},
        {label: translate("has-risks"), value: "has-risks", color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.AT_RISK]},
        {label: translate("out-of-way"), value: "out-of-way", color: STRATEGY_STATUS_COLORS[STRATEGY_STATUS.OFF_TRACK]},
    ];
}

function FilterEditPopover({
                               filters,
                               setFilters,
                               ownerOptions,
                               popoverRef,
                               isOwnerOverlayOpen,
                               setIsOwnerOverlayOpen,
                               onApply,
                               onCancel,
                               onReset,
                           }) {
    const translate = useTranslate();
    const statusOptions = getStrategyStatusOptions(translate);
    const toggleStatus = (status) => {
        setFilters({
            ...filters,
            status: filters.status === status ? undefined : status,
        });
    };

    return (
        <div
            ref={popoverRef}
            data-owner-overlay-open={isOwnerOverlayOpen}
            className="absolute z-5 bg-white border-1 shadow-4 p-3 rtl"
            style={{
                top: "5.4rem",
                left: "50%",
                transform: "translateX(-50%)",
                width: 340,
                borderColor: "#ead5ce",
                borderRadius: "1.25rem",
                fontFamily: "Shabnam-FD",
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex align-items-center  gap-2 font-bold text-800 mb-3 px-2">
                <Icon icon="mi mi:filter_alt" className="text-xl text-primary"/>
                {translate("strategy-map.filter.status-title")}
            </div>

            <div className="flex flex-column gap-1">
                {statusOptions.map(option => {
                    const active = filters.status === option.value;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            className={`border-none flex align-items-center justify-content-start gap-2 cursor-pointer border-round-xl px-3 py-2 transition-colors ${active ? "surface-100" : "bg-transparent hover:surface-50"}`}
                            style={{fontFamily: "Shabnam-FD", width: "100%"}}
                            onClick={() => toggleStatus(option.value)}
                        >
                            <span
                                className="border-circle flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                    width: 16,
                                    height: 16,
                                    border: `1px solid ${active ? option.color : "#e3bfb4"}`,
                                    background: active ? option.color : "#ffffff",
                                }}
                            >
                                {active && <Icon icon="mi mi:check" className="text-white text-xs"/>}
                            </span>

                            <span
                                className="inline-block border-circle flex-shrink-0"
                                style={{
                                    width: 10,
                                    height: 10,
                                    background: option.color,
                                }}
                            />

                            <span
                                className="text-xs"
                                style={{
                                    color: active ? option.color : "#191c1e",
                                    fontWeight: active ? 700 : 400,
                                }}
                            >
                                {option.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="my-3 border-bottom-1 surface-border opacity-50"/>

            <div className="px-2 relative ">
                <label className="block text-xs font-bold mb-2 text-700 ">
                    {translate("owner")}
                </label>
                <MultiSelect
                    value={filters.ownerIds}
                    options={ownerOptions}
                    onChange={(event) => setFilters({...filters, ownerIds: event.value})}
                    onShow={() => setIsOwnerOverlayOpen(true)}
                    onHide={() => setIsOwnerOverlayOpen(false)}
                    optionLabel="label"
                    optionValue="value"
                    display="comma"
                    filter
                    filterBy="label"
                    showSelectAll={false}
                    appendTo="self"
                    placeholder={translate("owner")}
                    selectedItemsLabel={`${filters.ownerIds?.length || 0} ${translate("owner")}`}
                    maxSelectedLabels={1}
                    panelClassName="rtl"
                    itemTemplate={(option) => (
                        <div className="flex align-items-center gap-2 w-full " style={{direction: "rtl"}}>
                            <Avatar memberData={option.owner?.member} size="small"/>
                            <span>{option.label}</span>
                        </div>
                    )}
                    className="w-full surface-50 border-round-xl"
                    pt={{
                        root: () => ({
                            className: "text-xs min-h-2rem flex align-items-center w-full p-2"
                        }),
                        label: () => ({className: "text-xs text-right w-full pr-2"}),
                        header: () => ({className: "p-2 border-bottom-1 surface-border"}),
                        filterInput: () => ({className: "text-right rtl"}),
                        checkboxContainer: () => ({className: "hidden"}),
                        closeButton: () => ({className: "hidden"}),
                        item: () => ({className: "p-2 text-xs hover:surface-100 border-round-md"}),
                        panel: () => ({className: "rtl mt-1"})
                    }}
                />
            </div>

            <div className="flex justify-content-end gap-2 mt-4 pt-3 border-top-1 border-100">
                <Button
                    type="button"
                    label={translate("cancel")}
                    outlined
                    className="p-button-rounded p-button-text shadow-none border-none text-xs"
                    onClick={onCancel}
                />
                <Button
                    type="button"
                    label={translate("apply-filter")}
                    className="p-button-rounded p-button-sm shadow-none border-none text-xs"
                    onClick={() => onApply(filters)}
                />
            </div>
        </div>
    );
}

function MissionNode({entity, onEditClick, readOnly}) {
    const translate = useTranslate();

    return (
        <div
            className="bg-white border-2 border-primary shadow-3 border-round-2xl rtl relative overflow-hidden"
            style={{
                width: "20rem",
                height: "11rem",
                boxSizing: "border-box",
            }}
        >
            <div
                className="bg-primary text-white px-3 py-2 flex  align-items-center"
                style={{
                    borderTopLeftRadius: "1rem",
                    borderTopRightRadius: "1rem",
                    direction: "rtl",
                }}
            >
                <div className="flex align-items-center gap-2">
                    <Icon icon="mi mi:flag" className="text-sm"/>
                    <span className="text-xs font-bold">{translate("strategy-map.mission.title")}</span>
                </div>
            </div>

            <div className="p-3">
                <div
                    className="font-bold text-right text-sm text-900"
                    style={{
                        minHeight: "4.25rem",
                        lineHeight: 1.8,
                        overflowWrap: "anywhere",
                    }}
                >
                    {entity?.title || translate("strategy-map.mission.default")}
                </div>
            </div>

            <div
                className="flex align-items-center justify-content-end border-top-1 px-3 py-2"
                style={{borderColor: "var(--primary-color)"}}
            >
                {!readOnly && (
                    <Button
                        type="button"
                        icon={<Icon icon="mi mi:edit" className="text-base"/>}
                        label={translate("edit")}
                        className="p-button-text p-button-sm p-button-rounded shadow-none text-xs gap-1 p-1 text-primary"
                        onClick={(event) => {
                            event.stopPropagation();
                            onEditClick?.();
                        }}
                    />
                )}
            </div>
        </div>
    );
}


function formatStrategyMapDate(value, translate) {
    if (!value) return translate("not-selected");

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return translate("not-selected");

    return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(date);
}

function ProjectNode({ entity, onDeleteClick, onDisconnectParent, onDisconnectGoals, readOnly }) {
    const translate = useTranslate();
    const menu = useRef();

    const statusColor = getStrategyStatusColor(entity.projectStatus);
    const statusBackgroundColor = withAlpha(statusColor, 0.12);
    // progress from milestones, not from the number of done tasks (docs/tasks/19-map-milestone-progress.md)
    const milestoneSummary = milestoneProgress(entity.deliverables);
    const progress = milestoneSummary.progress ?? 0;

    const menuItems = [
        {
            label: translate("strategy-map.action.disconnect-parent"),
            disabled: readOnly || !(entity.parentProjects || []).length,
            command: () => onDisconnectParent?.(entity),
        },
        {
            label: translate("strategy-map.action.disconnect-goals"),
            disabled: readOnly || !(entity.goals || []).length,
            command: () => onDisconnectGoals?.(entity),
        },
        {
            label: translate("archive-project"),
            disabled: readOnly,
            command: () => onDeleteClick?.(entity),
        },
    ];

    return (
	        <div
		            className="relative bg-white shadow-3 border-round-2xl border-2"            style={{
		            width: "20rem",
		            height: "14rem",
		            boxSizing: "border-box",
                    borderColor: statusColor,
		        }}
        >
            {/* Add parent button */}
            {/*<button*/}
            {/*    type="button"*/}
            {/*    className="absolute border-circle shadow-2 flex align-items-center justify-content-center cursor-pointer z-5 bg-white border-1 border-300"*/}
            {/*    style={{*/}
            {/*        top: "-0.85rem",*/}
            {/*        left: "50%",*/}
            {/*        transform: "translateX(-50%)",*/}
            {/*        width: "2rem",*/}
            {/*        height: "2rem",*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add" className="text-primary text-xl"/>*/}
            {/*</button>*/}

            {/* Header */}
            <div
                className="px-3 py-2 flex justify-content-between align-items-center text-900"                style={{
                borderTopLeftRadius: "1rem",
                borderTopRightRadius: "1rem",
                background: statusBackgroundColor,
            }}
            >
                <span className="text-3xs px-2 py-1 border-round-lg text-white"
                      style={{
                          whiteSpace: "nowrap",
                          background: statusColor,
                      }}
                >
                    {translate(entity.projectStatus)}
                </span>
                <div className="flex align-items-center gap-2">
                    <span className="text-xs font-bold">{translate("project")}</span>
                    <Icon icon="mi mi:crop_square" className="text-sm opacity-80" style={{color: statusColor}}/>
                </div>
            </div>

            {/* Body */}
            <div className="p-3">
                <div className="flex justify-content-between align-items-start gap-2 mb-3">
                    <span
                        className="font-bold text-right text-sm text-800"
                        style={{

                            lineHeight: 1.5,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {entity.title}
                    </span>
                    {!readOnly && (
                        <Button
                            type="button"
                            text
                            rounded
                            className="p-0 w-2rem h-2rem nodrag flex-shrink-0"
                            icon={<Icon icon="mi mi:more_vert" className="text-lg text-600"/>}
                            onClick={(event) => {
                                event.stopPropagation();
                                menu.current?.toggle(event);
                            }}
                        />
                    )}
                </div>
                <div
                    className=" surface-100 border-round-sm overflow-hidden"
                    style={{height: "8px"}}
                >
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            background: statusColor,
                        }}
                    />
                </div>
            </div>
            <div className="mb-3">
                <div className="flex justify-content-between align-items-center text-2xs mb-1  px-2 py-2 flex ">
                        <span className="text-500">
                            {milestoneSummary.milestones
                                ? `مایلستون ${milestoneSummary.doneMilestones.toLocaleString("fa-IR")} از ${milestoneSummary.milestones.toLocaleString("fa-IR")}`
                                : "بدون مایلستون"}
                        </span>
                    <div className="flex align-items-center gap-1">
                        <span className="font-bold text-700">{progress}% </span>
                        <Icon icon="mi mi:charger" className="text-sm opacity-80"/>
                    </div>
                </div>


                <div
                    className="flex align-items-center justify-content-between pt-2 border-top-1 border-50  mb-1 px-2 py-2  " style={{
                    borderColor: "#e3bfb4",
                }}>
                    <div className="flex align-items-center gap-2 text-2xs text-600" style={{minWidth: 0}}>
                        <span className="text-overflow-ellipsis overflow-hidden" style={{whiteSpace: "nowrap"}}>
                            {entity.owner?.member?.fullname || translate("not-selected")}
                        </span>
                        <Avatar memberData={entity.owner?.member} size="small"/>
                    </div>

                    <span className="text-2xs text-500 ltr" style={{whiteSpace: "nowrap"}}>
                        {formatStrategyMapDate(entity.dueDate, translate)}
                    </span>
                </div>
            </div>

            {/* Add child button */}
            {/*<button*/}
            {/*    type="button"*/}
            {/*    className="absolute border-circle shadow-2 flex align-items-center justify-content-center cursor-pointer z-5 bg-primary border-none"*/}
            {/*    style={{*/}
            {/*        bottom: "-0.85rem",*/}
            {/*        left: "50%",*/}
            {/*        transform: "translateX(-50%)",*/}
            {/*        width: "2rem",*/}
            {/*        height: "2rem",*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add" className="text-white text-xl"/>*/}
            {/*</button>*/}

            {/* Add child label */}

            {/*<div*/}
            {/*    className="absolute flex align-items-center gap-1 text-primary cursor-pointer hover:underline text-2xs font-bold"*/}
            {/*    style={{*/}
            {/*        right: 0,*/}
            {/*        bottom: "-1.9rem",*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add_circle"/>*/}
            {/*    <span>{translate("add-child")}</span>*/}
            {/*</div>*/}


            <Menu
                model={menuItems}
                popup
                ref={menu}
                pt={{
                    root: () => ({className: "w-fit p-1"}),
                    menuitem: () => ({className: "text-sm rtl"}),
                }}
            />
        </div>
    );
}


// function ProjectNode({ entity, onDeleteClick }) {
//     const project = useProject();
//     const translate = useTranslate();
//     const menu = useRef();
//
//
//     const getStatusTheme = (status) => {
//         switch (status) {
//             case "out-of-way": return { bg: "bg-red-500", border: "border-red-500", text: "text-white" };
//             case "has-risks": return { bg: "bg-pink-500", border: "border-pink-500", text: "text-white" };
//             default: return { bg: "bg-green-500", border: "border-green-500", text: "text-white" };
//         }
//     };
//
//     const theme = getStatusTheme(entity.projectStatus);
//     const progress = getProjectProgress(entity);
//
//     return (
//         <div className={`node-wrapper bg-white shadow-3 border-round-2xl relative w-20rem border-2 ${theme.border}`} style={{ minHeight: '160px' }}>
//             {/* دکمه افزودن والد (شناور بالا) */}
//             <button className="absolute top-0 left-50 translate-x-neg-50 translate-y-neg-50 w-2rem h-2rem bg-white border-1 border-300 border-circle shadow-2 flex align-items-center justify-center cursor-pointer z-5 hover:scale-110 transition-transform">
//                 <Icon icon="mi mi:add" className="text-primary text-xl" />
//             </button>
//
//             {/* هدر رنگی کارت */}
//             <div className={`${theme.bg} ${theme.text} px-3 py-2 flex justify-content-between align-items-center border-round-top-xl`}>
//                 <div className="flex align-items-center gap-2">
//                     <span className="text-xs font-bold">{translate("project")}</span>
//                     <Icon icon="mi mi:info" className="text-sm opacity-80" />
//                 </div>
//                 <span className="text-2xs bg-white-alpha-20 px-2 py-1 border-round-lg">
//                     {translate(entity.projectStatus)}
//                 </span>
//             </div>
//
//             <div className="p-3">
//                 <div className="flex justify-content-between align-items-start mb-3">
//                     <span className="font-bold text-sm line-height-3 text-800">{entity.title}</span>
//                     <Icon icon="mi mi:more_vert" className="text-xl cursor-pointer nodrag" onClick={(e) => menu.current.toggle(e)} />
//                 </div>
//
//                 {/* پراگرس بار خطی */}
//                 <div className="mb-3">
//                     <div className="flex justify-content-between text-2xs mb-1">
//                         <span className="text-500">{entity.tasks?.length || 0} {translate("tasks")}</span>
//                         <span className="font-bold">{progress}%</span>
//                     </div>
//                     <div className="surface-100 border-round-sm overflow-hidden" style={{ height: '8px' }}>
//                         <div className={theme.bg} style={{ width: `${progress}%`, height: '100%' }} />
//                     </div>
//                 </div>
//
//                 <div className="flex align-items-center justify-content-between pt-2 border-top-1 border-50">
//                     <div className="flex align-items-center gap-2 text-2xs text-600">
//                         <Avatar memberData={entity.owner} size="small" />
//                         <span>{entity.owner?.fullname}</span>
//                     </div>
//                     <span className="text-2xs text-500 ltr">{formatStrategyMapDate(entity.dueDate, translate)}</span>
//                 </div>
//             </div>
//
//             {/* دکمه افزودن فرزند (شناور پایین) */}
//             <button className="absolute bottom-0 left-50 translate-x-neg-50 translate-y-50 w-2rem h-2rem bg-primary border-none border-circle shadow-2 flex align-items-center justify-center cursor-pointer z-5 hover:scale-110 transition-transform">
//                 <Icon icon="mi mi:add" className="text-white text-xl" />
//             </button>
//
//             {/* لیبل "افزودن بچه" در گوشه */}
//             <div className="absolute -bottom-30 right-0 flex align-items-center gap-1 text-primary cursor-pointer hover:underline text-2xs font-bold">
//                 <Icon icon="mi mi:add_circle" />
//                 <span>{translate("add-child")}</span>
//             </div>
//
//             <Menu model={[{label: translate("delete"), command: () => onDeleteClick(entity)}]} popup ref={menu} />
//         </div>
//     );
// }


// function ProjectNode({entity, onDeleteClick}) {
//     const project = useProject();
//     const translate = useTranslate();
//     const menu = useRef();
//
//     const menuItems = [
//         {
//             label: translate("delete"),
//             command: () => {
//                 onDeleteClick?.(entity);
//             },
//         },
//     ];
//
//
//     const borderClassName = getProjectHealthBorderClassName(entity.projectStatus);
//
//     const normalizedProgress = getProjectProgress(entity);
//
//     const ownerName = entity.owner?.fullname || translate("not-selected");
//     const teamName = entity.team?.title || entity.team?.name || translate("not-selected");
//     const dueDate = entity.dueDate || entity.deliverAt || entity.endDate || entity.deadline;
//     const dueDateText = formatStrategyMapDate(dueDate, translate);
//
//     return (
//         <div className={`bg-white shadow-1 border-round-lg text-xs rtl relative w-16rem border-1 ${borderClassName}`}>
//             <div className="flex justify-content-end p-1">
//                 <span className={`${project.statusClassName(entity.projectStatus, true, true)} text-3xs`}>
//                     {translate(entity.projectStatus)}
//                 </span>
//             </div>
//
//             <div className="p-2">
//                 <div className="flex align-items-center justify-content-between gap-2">
//                     <span className="font-bold line-height-3">
//                         {entity.title}
//                     </span>
//
//                     <Icon
//                         icon="mi mi:more_vert"
//                         className="text-lg nodrag"
//                         onClick={(event) => {
//                             event.stopPropagation();
//                             menu.current.toggle(event);
//                         }}
//                     />
//
//                     <Menu
//                         pt={{
//                             root: () => ({className: "w-fit p-1"}),
//                             menuitem: () => ({className: "text-sm rtl"}),
//                         }}
//                         model={menuItems}
//                         popup
//                         ref={menu}
//                     />
//                 </div>
//
//                 <Divider className="my-2"/>
//
//                 <div className="flex flex-column gap-2">
//                     <div className="flex align-items-center justify-content-between gap-2">
//                         <span className="text-500">{translate("owner")}</span>
//                         <span className="font-semibold text-left">{ownerName}</span>
//                     </div>
//
//                     <div className="flex align-items-center justify-content-between gap-2">
//                         <span className="text-500">{translate("team")}</span>
//                         <span className="font-semibold text-left">{teamName}</span>
//                     </div>
//
//                     <div className="flex align-items-center justify-content-between gap-2">
//                         <span className="text-500">{translate("dueDate")}</span>
//                         <span className="font-semibold">{dueDateText}</span>                    </div>
//
//                     <div className="flex flex-column gap-1">
//                         <div className="flex align-items-center justify-content-between">
//                             <span className="text-500">{translate("progress")}</span>
//                             <span className="font-semibold ltr">{normalizedProgress}%</span>
//                         </div>
//
//                         <div className="surface-200 border-round overflow-hidden" style={{height: "8px"}}>
//                             <div
//                                 className="bg-primary border-round"
//                                 style={{
//                                     height: "8px",
//                                     width: `${normalizedProgress}%`,
//                                 }}
//                             />
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }

function GoalNode({ entity, onDeleteClick, onDisconnectParent, readOnly }) {
    const goal = useGoal();
    const translate = useTranslate();
    const menu = useRef();

    const statusColor = getStrategyStatusColor(entity.goalStatus);
    const statusBackgroundColor = withAlpha(statusColor, 0.12);
    const normalizedStatus = normalizeStrategyStatus(entity.goalStatus);
    const projectsCount = entity.projects?.length || 0;
    const progress = Math.min(100, projectsCount * 10);

    const menuItems = [
        {
            label: translate(entity.parentGoal?.id || entity.parentGoal?.documentId ? "strategy-map.action.disconnect-parent" : "strategy-map.action.disconnect-mission"),
            disabled: readOnly || Boolean(entity.isOrphan),
            command: () => onDisconnectParent?.(entity),
        },
        {
            label: translate("archive-goal"),
            disabled: readOnly,
            command: () => onDeleteClick?.(entity),
        },
    ];

    return (
        <div
            className="relative bg-white shadow-3 border-round-2xl border-2"
            style={{
                width: "20rem",
                height: "17rem",
                boxSizing: "border-box",
                borderColor: statusColor,
            }}
        >
            {/* Add parent button (Top center) */}
            {/*<button*/}
            {/*    type="button"*/}
            {/*    className="absolute border-circle shadow-2 flex align-items-center justify-content-center cursor-pointer z-5 bg-white border-1 border-300"*/}
            {/*    style={{*/}
            {/*        top: "-0.85rem",*/}
            {/*        left: "50%",*/}
            {/*        transform: "translateX(-50%)",*/}
            {/*        width: "2rem",*/}
            {/*        height: "2rem",*/}
            {/*    }}*/}
            {/*    onClick={(e) => {*/}
            {/*        e.stopPropagation();*/}
            {/*        onAddParent?.(entity);*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add" className="text-primary text-xl" />*/}
            {/*</button>*/}

            {/* Header: status badge (left) + type (right) */}
            <div
                className="px-3 py-2 flex justify-content-between align-items-center text-900"
                style={{
                    borderTopLeftRadius: "1rem",
                    borderTopRightRadius: "1rem",
                    background: statusBackgroundColor,
                }}
            >
                {/* badge بالا-چپ */}
                <span
                    className="text-3xs px-2 py-1 border-round-lg text-white"
                    style={{
                        whiteSpace: "nowrap",
                        background: statusColor,
                    }}
                >
                    {translate(entity.goalStatus)}
                </span>

                {/* نوع کارت بالا-راست */}
                <div className="flex align-items-center gap-2">
                    <span className="text-xs font-bold">{translate("goal")}</span>
                    <Icon icon="mi mi:change_history" className="text-sm opacity-80" style={{color: statusColor}}/>                </div>
            </div>

            {/* Body: title + menu + progress */}
            <div className="p-3">
                <div className="flex justify-content-between align-items-start gap-2 mb-3">
                    <span
                        className="font-bold text-right text-sm text-800"
                        style={{
                            lineHeight: 1.5,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {entity.title}
                    </span>
                    {!readOnly && (
                        <Button
                            type="button"
                            text
                            rounded
                            className="p-0 w-2rem h-2rem nodrag flex-shrink-0"
                            icon={<Icon icon="mi mi:more_vert" className="text-lg text-600"/>}
                            onClick={(event) => {
                                event.stopPropagation();
                                menu.current?.toggle(event);
                            }}
                        />
                    )}

                </div>

                {/* progress bar */}
                <div className="surface-100 border-round-sm overflow-hidden" style={{ height: "8px" }}>
                    <div
                        style={{
                            width: `${progress}%`,
                            height: "100%",
                            background: statusColor,
                        }}
                    />
                </div>
            </div>

            {projectsCount > 0 && [STRATEGY_STATUS.AT_RISK, STRATEGY_STATUS.OFF_TRACK].includes(normalizedStatus) && (
                <div
                    className="flex align-items-center gap-2 mt-3 text-sm font-bold"
                    style={{
                        color: statusColor,
                    }}
                >
                    <Icon icon="mi mi:error_outline" className="text-lg"/>
                    <span>{projectsCount} {translate("project")} {translate("out-of-way")}</span>
                </div>
            )}

            {/* Footer: metrics + owner + extra info */}
            <div className="mb-3">
                <div className="flex justify-content-between align-items-center text-2xs mb-1 px-2 py-2" >
                    <span className="text-500">
{projectsCount} {translate("project")}
                    </span>

                    <div className="flex align-items-center gap-1">
                        <span className="font-bold text-700">{progress}%</span>
                        <Icon icon="mi mi:charger" className="text-sm opacity-80" />
                    </div>
                </div>

                <div className="flex align-items-center justify-content-between pt-2 border-top-1 border-100 mb-1 px-2 py-2">
                    {/* طبق عکس: نام + آواتار */}
                    <div className="flex align-items-center gap-2 text-2xs text-600" style={{ minWidth: 0 }}>
                        <span className="text-overflow-ellipsis overflow-hidden" style={{ whiteSpace: "nowrap" }}>
                            {entity.owner?.member?.fullname || translate("not-selected")}
                        </span>
                        <Avatar memberData={entity.owner?.member} size="small" />
                    </div>

                    {/* اطلاعات تکمیلی سمت راست (مثل workspace یا تاریخ) */}
                    <div className="flex gap-1 align-items-center text-400" style={{ whiteSpace: "nowrap" }}>
                        <Icon icon="mi mi:folder_open" className="text-sm" />
                        <span className="text-2xs">
                            {projectsCount} {translate("project")}
                        </span>
                    </div>
                </div>
            </div>

            {/* Add child button (Bottom center) */}
            {/*<button*/}
            {/*    type="button"*/}
            {/*    className="absolute border-circle shadow-2 flex align-items-center justify-content-center cursor-pointer z-5 bg-primary border-none"*/}
            {/*    style={{*/}
            {/*        bottom: "-0.85rem",*/}
            {/*        left: "50%",*/}
            {/*        transform: "translateX(-50%)",*/}
            {/*        width: "2rem",*/}
            {/*        height: "2rem",*/}
            {/*    }}*/}
            {/*    onClick={(e) => {*/}
            {/*        e.stopPropagation();*/}
            {/*        onAddChild?.(entity);*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add" className="text-white text-xl" />*/}
            {/*</button>*/}

            {/* Add child label (Bottom-right) */}
            {/*<div*/}
            {/*    className="absolute flex align-items-center gap-1 text-primary cursor-pointer hover:underline text-2xs font-bold"*/}
            {/*    style={{*/}
            {/*        right: 0,*/}
            {/*        bottom: "-1.9rem",*/}
            {/*    }}*/}
            {/*    onClick={(e) => {*/}
            {/*        e.stopPropagation();*/}
            {/*        onAddChild?.(entity);*/}
            {/*    }}*/}
            {/*>*/}
            {/*    <Icon icon="mi mi:add_circle" />*/}
            {/*    <span>{translate("add-child")}</span>*/}
            {/*</div>*/}

            <Menu
                model={menuItems}
                popup
                ref={menu}
                pt={{
                    root: () => ({ className: "w-fit p-1" }),
                    menuitem: () => ({ className: "text-sm rtl" }),
                }}
            />
        </div>
    );
}


// function GoalNode({entity, onDeleteClick}) {
//     const goal = useGoal();
//     const translate = useTranslate();
//     const menu = useRef();
//     const menuItems = [
//         {label:translate("delete"), command:()=>{onDeleteClick?.(entity)}}
//     ]
//     return (
//         <div className="bg-white shadow-1 border-round-lg text-xs rtl relative w-18rem">
//             <div className="bg-primary-500 text-white border-round-2xl px-2 py-1 absolute mr-2 flex align-items-start" style={{marginTop:"-12px"}}>
//                 <Icon icon="mi mi:flag" className="text-xs cursor-pointer"/>
//                 <span className="text-2xs font-bold">{translate("goal")}</span>
//             </div>
//             <div className="flex justify-content-end p-1">
//                 <span className={`${goal.statusClassName(entity.goalStatus, true, true)} text-3xs`}>{translate(entity.goalStatus)}</span>
//             </div>
//             <div className={"p-2 pt-4"}>
//                 <div className="flex align-items-center justify-content-between">
//                     <div className="flex align-items-center gap-2">
//                         <span className="font-bold">{entity.title}</span>
//                     </div>
//                     <Icon icon="mi mi:more_vert" className="text-lg nodrag" onClick={(e)=>{e.stopPropagation();menu.current.toggle(e)}}/>
//                     <Menu pt={{root:()=>({className:"w-fit p-1"}), menuitem:()=>({className:"text-sm rtl"})}} model={menuItems} popup ref={menu} />
//                 </div>
//                 <Divider className="my-2"/>
//                 <div className="flex align-items-center justify-content-between">
//                     <Avatar memberData={entity.owner}/>
//                     {
//                         entity.projects?.length>0&&(
//                             <div className="flex gap-1 align-items-center text-400">
//                                 <Icon icon="mi mi:folder_open" className="text-sm"/>
//                                 <span className="text-2xs">{`${entity.projects?.length} ${translate("project")}`}</span>
//                             </div>
//                         )
//                     }
//                 </div>
//             </div>
//         </div>
//     );
// }

// const BaseNode = ({data, template, onDeleteClick, onEditClick}) => {
//     const NodeTemplate = template;
//
//     return(
//         <>
//             <Handle style={{ width: 16, height: 16 }} id="top" type="target" position={Position.Top} isConnectableStart isConnectableEnd />
//             <NodeTemplate {...data} onDeleteClick={onDeleteClick} onEditClick={onEditClick}/>
//             <Handle style={{ width: 16, height: 16 }} id="bottom" type="source" position={Position.Bottom} isConnectableStart isConnectableEnd />
//         </>
//     )
// };


function OrphanProjectsSectionNode({entity}) {
    const translate = useTranslate();

    return (
        <div
            className="rtl bg-white border-1 shadow-1 px-4 py-2 text-sm font-bold text-700"
            style={{
                borderColor: "#ead5ce",
                borderRadius: "1rem",
                minWidth: "14rem",
                textAlign: "center",
            }}
        >
            {translate(entity?.titleKey || "strategy-map.orphan-projects.title")}
        </div>
    );
}

const BaseNode = ({data, type, template, onDeleteClick, onEditClick, onAddParent, onAddChild, onDisconnectParent, onDisconnectGoals, disableAddParent, disableAddChild, templateReadOnly, readOnly}) => {
    const translate = useTranslate();
    const NodeTemplate = template;
    const entity = data?.entity || {};

    const isMission = type === "mission";
    const isRootGoal = type === "goal" && !entity.parentGoal?.id && !entity.isOrphan;
    const isProject = type === "project";

    const canAddParent = !readOnly && !disableAddParent && (isProject || (type === "goal" && !isRootGoal));
    const canAddChild = !readOnly && !disableAddChild;

    return (
        <div className="relative" style={{overflow: "visible"}}>
            {canAddParent && (
                <>
                    <button
                        type="button"
                        className="absolute border-circle shadow-3 flex align-items-center justify-content-center cursor-pointer z-5 bg-white border-1 nodrag"
                        style={{
                            top: "-1rem",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "2.25rem",
                            height: "2.25rem",
                            borderColor: "var(--primary-color)",
                            color: "var(--primary-color)",
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onAddParent?.(entity, type);
                        }}
                    >
                        <Icon icon="mi mi:add" className="text-xl"/>
                    </button>

                    <button
                        type="button"
                        className="absolute border-none bg-transparent flex align-items-center gap-1 cursor-pointer z-5 nodrag text-xs font-bold"
                        style={{
                            top: "-2.2rem",
                            right: "1.75rem",
                            color: "var(--primary-color)",
                            pointerEvents: "auto",
                            fontFamily: "Shabnam-FD",
                            fontSize: "0.75rem",
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onAddParent?.(entity, type);
                        }}
                    >
                        <Icon icon="mi mi:add_circle" className="text-base"/>
                        <span>{translate("add-parent")}</span>
                    </button>
                </>
            )}

            {!isMission && (
                <Handle
                    style={{width: 14, height: 14, opacity: 1, background: "var(--surface-card)", border: "2px solid var(--primary-color)"}}
                    id="top"
                    type="target"
                    position={Position.Top}
                    isConnectableStart
                    isConnectableEnd
                    isConnectable={!readOnly}
                />
            )}

            <NodeTemplate
                {...data}
                onDeleteClick={onDeleteClick}
                onEditClick={onEditClick}
                onDisconnectParent={onDisconnectParent}
                onDisconnectGoals={onDisconnectGoals}
                readOnly={templateReadOnly ?? readOnly}
            />

            <Handle
                style={{width: 14, height: 14, opacity: 1, background: "var(--surface-card)", border: "2px solid var(--primary-color)"}}
                id="bottom"
                type="source"
                position={Position.Bottom}
                isConnectableStart
                isConnectableEnd
                isConnectable={!readOnly}
            />

            {canAddChild && (
                <>
                    <button
                        type="button"
                        className="absolute border-circle shadow-3 flex align-items-center justify-content-center cursor-pointer z-5 bg-white border-1 nodrag"
                        style={{
                            bottom: "-1rem",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "2.25rem",
                            height: "2.25rem",
                            borderColor: "var(--primary-color)",
                            color: "var(--primary-color)",
                        }}
                        onClick={(event) => {
                            event.stopPropagation();
                            onAddChild?.(entity, type);
                        }}
                    >
                        <Icon icon="mi mi:add" className="text-xl"/>
                    </button>

                    {!isMission && (
                        <button
                            type="button"
                            className="absolute border-none bg-transparent flex align-items-center gap-1 cursor-pointer z-5 nodrag text-xs font-bold"
                            style={{
                            bottom: "-2.1rem",
                            right: 0,
                                color: "var(--primary-color)",
                                fontFamily: "Shabnam-FD",
                                fontSize: "0.75rem",
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onAddChild?.(entity, type);
                            }}
                        >
                            <Icon icon="mi mi:add_circle" className="text-base"/>
                            <span>{translate("add-child")}</span>
                        </button>
                    )}
                </>
            )}
        </div>
    );
};


export {normalizeGraph};

export default StrategyMap;
